/**
 * Date helper — avoid "day jump" when sending dates to API.
 * Use toYMD / toMySQLDateTime in payloads; parseDateSafe / parseDateTimeSafe when reading from API.
 *
 * Problem: If user picks 2026-02-10 in UTC+2, Date is 2026-02-10 00:00:00 local
 * → toISOString() gives 2026-02-09T22:00:00.000Z → server date('Y-m-d', strtotime(...)) yields 2026-02-09 (one day back).
 *
 * Solution: Always send YYYY-MM-DD or YYYY-MM-DD HH:mm:ss using local date components. Never use toISOString().slice(0,10) for date-only fields.
 */

/**
 * Format Date or date string to YYYY-MM-DD (local date, no UTC shift). Use this in API payloads for date-only fields.
 * @param {Date|string|null} d
 * @returns {string|null}
 */
export function toYMD(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : parseDateSafe(d);
  if (!dt) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Calendar date → YYYY-MM-DD for API payloads (filters, forms). Empty/falsy → ''.
 * Uses toYMD / parseDateSafe — never toISOString().slice(0, 10).
 * @param {Date|string|null|undefined} value
 * @returns {string}
 */
export function toApiDate(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) return toYMD(value) || '';
  const parsed = parseDateSafe(value);
  if (parsed) return toYMD(parsed) || '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

/**
 * Format date-only value for US display (MM/DD/YYYY).
 * Uses parseDateSafe — never `new Date('YYYY-MM-DD')` (UTC shift risk).
 * @param {Date|string|null|undefined} value
 * @returns {string} empty string for null/invalid
 */
export function formatDateUs(value) {
  const parsed = parseDateSafe(value);
  if (!parsed) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${month}/${day}/${parsed.getFullYear()}`;
}

/**
 * Parse YYYY-MM-DD (or string starting with YYYY-MM-DD) to Date without timezone conversion.
 * Use when binding API response or stored value to a date picker.
 * @param {string|Date|null} dateString
 * @returns {Date|null}
 */
export function parseDateSafe(dateString) {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  const datePart = String(dateString).substring(0, 10);
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
  return date;
}

/**
 * Format Date to YYYY-MM-DD HH:MM:SS (local) for datetime fields. Use in API payloads for datetime.
 * @param {Date|string|null} date
 * @returns {string|null}
 */
export function toMySQLDateTime(date) {
  if (!date) return null;
  const dt = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dt.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

/**
 * True when API value is a calendar date only (no time-of-day).
 * @param {string|Date|null} value
 * @returns {boolean}
 */
export function isDateOnlyApiValue(value) {
  if (value == null || value === '') return false;
  if (value instanceof Date) return false;
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * True when value includes a time component (DB datetime or ISO).
 * @param {string|Date|null} value
 * @returns {boolean}
 */
export function hasDateTimeComponent(value) {
  if (value == null || value === '') return false;
  if (value instanceof Date) return true;
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:/.test(s) || /[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
}

/**
 * Parse API/DB datetime stored as UTC (naive YYYY-MM-DD HH:mm:ss or ISO with Z).
 * @param {string|Date|null} dateTimeString
 * @returns {Date|null}
 */
export function parseUtcDateTime(dateTimeString) {
  if (!dateTimeString) return null;
  if (dateTimeString instanceof Date) {
    return Number.isNaN(dateTimeString.getTime()) ? null : dateTimeString;
  }
  const s = String(dateTimeString).trim();
  if (!s) return null;
  if (/[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const d = new Date(Date.UTC(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      parseInt(m[6] || '0', 10)
    ));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Parse MySQL datetime (YYYY-MM-DD HH:MM:SS) or ISO with T to Date in local time (no UTC conversion of the value).
 * @param {string|null} dateTimeString
 * @returns {Date|null}
 */
export function parseDateTimeSafe(dateTimeString) {
  if (!dateTimeString) return null;
  if (dateTimeString instanceof Date) return dateTimeString;
  const m = dateTimeString.match(/^(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/);
  if (m) {
    const date = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10), parseInt(m[4], 10), parseInt(m[5], 10), parseInt(m[6], 10));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return parseDateSafe(dateTimeString);
}

/**
 * Add calendar months to a local date (handles month length / leap years like Date#setMonth).
 * @param {Date|string|null} date
 * @param {number} months
 * @returns {Date|null}
 */
export function addCalendarMonths(date, months) {
  const d = date instanceof Date ? new Date(date.getTime()) : parseDateSafe(date);
  if (!d) return null;
  const day = d.getDate();
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setMonth(out.getMonth() + months);
  if (out.getDate() !== day) {
    out.setDate(0);
  }
  return out;
}

/**
 * Inclusive end date for a 12-month plan window: (periodFrom + 12 months) − 1 day.
 * @param {Date|string|null} periodFrom
 * @returns {Date|null}
 */
export function inclusiveEndAfterTwelveMonthsFrom(periodFrom) {
  const end = addCalendarMonths(periodFrom, 12);
  if (!end) return null;
  end.setDate(end.getDate() - 1);
  return end;
}

/**
 * Default 12-month inclusive plan window: anchor day (default today local) through end = anchor + 12 months − 1 day.
 * @param {Date} [anchorDate] defaults to now (local calendar day)
 * @returns {{ from: Date, to: Date }}
 */
export function defaultTwelveMonthPlanPeriodFromDate(anchorDate = new Date()) {
  const base = anchorDate instanceof Date ? anchorDate : parseDateSafe(anchorDate);
  const ref = base && !Number.isNaN(base.getTime()) ? base : new Date();
  const from = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const to = inclusiveEndAfterTwelveMonthsFrom(from);
  if (!to) {
    return { from, to: from };
  }
  return { from, to };
}
